import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { subscription } from "./featureApi";
interface SubscriptionState {
  loading: "idle" | "pending" | "succeeded" | "failed";
  createdPlan: { id: number; name: string } | null;
  sessionUrl: string;
  sessionId: string;
  error: string;
  allPlans: any;
  planData: any;
  data: any;
  allSubscriptions?: {
    subscriptions: any[];
    nextCursor: any;
    previousCursor: any;
    hasMore: any;
  };
}

const initialState: SubscriptionState = {
  loading: "idle",
  createdPlan: null,
  data: null,
  planData: null,
  sessionUrl: "",
  sessionId: "",
  error: "",
  allPlans: {
    plans: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  },
  allSubscriptions: {
    subscriptions: [],
    nextCursor: null,
    previousCursor: null,
    hasMore: false,
  },
};

interface CancelSubscriptionPayload {
  userId: number;
  subscriptionId: string;
  option: "immediate" | "end_of_period" | "custom_date";
  customDate?: string;
}

interface RefundSubscriptionPayload {
  payment_id: string;
  amount: any;
}

interface CancelSubscriptionResponse {
  success: boolean;
  message?: string;
  [key: string]: any;
}

interface RefundSubscriptionResponse {
  success: boolean;
  message?: string;
  [key: string]: any;
}

export const paySubscription = createAsyncThunk<
  string,
  { name: string; priceId: string; trial_periode: number },
  { rejectValue: { message: string; sessionUrl?: string } }
>("subscription/paySubscription", async (data, { rejectWithValue }) => {
  try {
    const response = await subscription.paySubscription(data);
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message: error.message || "Failed to initiate subscription",
      sessionUrl: error.sessionUrl,
    });
  }
});

export const createPlan = createAsyncThunk<{ id: number; name: string }, { data: any }, { rejectValue: string }>(
  "subscription/createPlan",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await subscription.createPlan(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create plan");
    }
  }
);

export const cancelSubscription = createAsyncThunk<
  CancelSubscriptionResponse,
  CancelSubscriptionPayload,
  { rejectValue: string }
>(
  "subscription/cancelSubscription",
  async ({ userId, subscriptionId, option, customDate }, { rejectWithValue }) => {
    try {
      const response = await subscription.cancelSubscriptionService({
        userId,
        subscriptionId,
        option,
        customDate,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to cancel subscription");
    }
  }
);

export const refundSubscription = createAsyncThunk<
  RefundSubscriptionResponse,
  RefundSubscriptionPayload,
  { rejectValue: string }
>("subscription/refundSubscription", async ({ payment_id, amount }, { rejectWithValue }) => {
  try {
    const response = await subscription.refundSubscriptionService({
      payment_id,
      amount,
    });
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to cancel subscription");
  }
});

export const adminGetAllPlans = createAsyncThunk(
  "adminGetAllPlans",
  async ({ page, searchTerm }: { page: number; searchTerm: string }) => {
    return await subscription.adminGetAllPlans(page, searchTerm);
  }
);

export const adminDeletePlan = createAsyncThunk(
  "adminDeletePlan",
  async ({ id, product_id }: { id: number; product_id: string }) => {
    return await subscription.adminDeletePlan(id, product_id);
  }
);

export const adminGetAllSubscriptions = createAsyncThunk(
  "adminGetAllSubscriptions",
  async ({
    limit = 10,
    starting_after,
    ending_before,
    email,
    status,
  }: {
    limit?: number;
    starting_after?: string;
    ending_before?: string;
    email?: string;
    status?: string;
  }) => {
    return await subscription.adminGetAllSubscriptions({
      limit,
      starting_after,
      ending_before,
      email,
      status,
    });
  }
);

export const getAllPlans = createAsyncThunk("getAllPlans", async () => {
  return await subscription.getAllPlans();
});

export const getCustomerSubscription = createAsyncThunk("getCustomerSubscription", async () => {
  return await subscription.getCustomerSubscription();
});

export const getSubscriptionDetails = createAsyncThunk("getSubscriptionDetails", async () => {
  return await subscription.getSubscriptionDetails();
});

export const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    resetPaymentState: (state) => {
      state.loading = "idle";
      state.createdPlan = null;
      state.sessionUrl = "";
      state.sessionId = "";
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(paySubscription.pending, (state) => {
        state.loading = "pending";
        state.error = "";
      })
      .addCase(paySubscription.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.sessionUrl = action.payload;
      })
      .addCase(paySubscription.rejected, (state, action) => {
        state.loading = "failed";
        //state.error = action.payload || "An error occurred";
      })

      // CreatePlan cases
      .addCase(createPlan.pending, (state) => {
        state.loading = "pending";
        state.error = "";
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.createdPlan = action.payload;
      })
      .addCase(createPlan.rejected, (state, action) => {
        state.loading = "failed";
      });

    // Admin Get All Plans
    builder
      .addCase(adminGetAllPlans.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(adminGetAllPlans.fulfilled, (state, action) => {
        state.allPlans = {
          plans: action.payload.plans || [],
          totalCount: action.payload.totalCount || 0,
          totalPages: action.payload.totalPages || 0,
          currentPage: action.payload.currentPage || 1,
        };
        state.loading = "succeeded";
      })
      .addCase(adminGetAllPlans.rejected, (state, action) => {
        state.loading = "failed";
      });

    //  Get All Plans
    builder
      .addCase(getAllPlans.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getAllPlans.fulfilled, (state, action) => {
        state.allPlans = {
          plans: action.payload.plans || [],
        };
        state.loading = "succeeded";
      })
      .addCase(getAllPlans.rejected, (state, action) => {
        state.loading = "failed";
      });

    //  Get customer Plan details
    builder
      .addCase(getCustomerSubscription.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getCustomerSubscription.fulfilled, (state, action) => {
        state.planData = action.payload;
        state.loading = "succeeded";
      })
      .addCase(getCustomerSubscription.rejected, (state, action) => {
        state.loading = "failed";
      });
    builder
      .addCase(adminGetAllSubscriptions.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(adminGetAllSubscriptions.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.allSubscriptions = {
          subscriptions: action.payload.subscriptions || [],
          nextCursor: action.payload.next_cursor || null,
          previousCursor: action.payload.previous_cursor || null,
          hasMore: action.payload.has_more || false,
        };
      })
      .addCase(adminGetAllSubscriptions.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message || "Something went wrong";
      });

    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.data = action.payload;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload || "Failed to cancel subscription";
      });

    // delete

    builder
      .addCase(adminDeletePlan.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(adminDeletePlan.fulfilled, (state, action) => {
        state.planData = action.payload;

        state.loading = "succeeded";
      })
      .addCase(adminDeletePlan.rejected, (state, action) => {
        state.loading = "failed";
      });

    builder
      .addCase(getSubscriptionDetails.pending, (state) => {
        state.loading = "pending";
        state.error = "";
      })
      .addCase(getSubscriptionDetails.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.sessionUrl = action.payload;
      })
      .addCase(getSubscriptionDetails.rejected, (state, action) => {
        state.loading = "failed";
      });
  },
});

export const { resetPaymentState } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
